import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, CheckCircle2, AlertTriangle } from "lucide-react";
import Logo from "@/components/Logo";
import api from "@/lib/api";

export default function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token") || "";
    const [form, setForm] = useState({ password: "", confirm: "" });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (form.password.length < 8) {
            setError("A nova senha deve ter pelo menos 8 caracteres.");
            return;
        }
        if (form.password !== form.confirm) {
            setError("As senhas não conferem.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/api/auth/reset-password", { token, new_password: form.password });
            setDone(true);
            toast.success("Senha redefinida com sucesso!");
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === "string" ? detail : "Não foi possível redefinir. Solicite um novo link.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex min-h-[80vh] items-center justify-center bg-forest-deep px-4" data-testid="reset-password-page">
            <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-white/[0.06] p-8 backdrop-blur-xl">
                <div className="flex justify-center">
                    <Logo />
                </div>

                {!token ? (
                    <div className="mt-6 text-center">
                        <AlertTriangle className="mx-auto h-8 w-8 text-gold" />
                        <h1 className="mt-4 font-serif text-2xl font-semibold text-white">Link inválido</h1>
                        <p className="mt-2 text-xs leading-relaxed text-stone-400">
                            Este link de redefinição está incompleto. Solicite um novo na tela de acesso ao painel.
                        </p>
                    </div>
                ) : done ? (
                    <div className="mt-6 text-center" data-testid="reset-success">
                        <CheckCircle2 className="mx-auto h-9 w-9 text-emerald-400" />
                        <h1 className="mt-4 font-serif text-2xl font-semibold text-white">Senha redefinida!</h1>
                        <p className="mt-2 text-xs leading-relaxed text-stone-400">
                            Sua nova senha já está ativa. As sessões anteriores foram encerradas por segurança.
                        </p>
                        <Link
                            to="/admin"
                            data-testid="reset-go-admin"
                            className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-sm font-semibold text-forest-deep transition-all duration-200 hover:bg-sun active:scale-[0.98]"
                        >
                            Ir para o painel
                        </Link>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <h1 className="mt-6 text-center font-serif text-2xl font-semibold text-white">Criar nova senha</h1>
                        <p className="mt-1 text-center text-xs text-stone-400">Painel administrativo · Destino Resorts</p>

                        <label className="mt-7 block text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-400" htmlFor="reset-password">
                            Nova senha
                        </label>
                        <input
                            id="reset-password"
                            data-testid="reset-password-input"
                            type="password"
                            required
                            minLength={8}
                            value={form.password}
                            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                            className="mt-2 w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-stone-500 focus:border-gold"
                            placeholder="Mínimo 8 caracteres"
                            autoComplete="new-password"
                        />

                        <label className="mt-4 block text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-400" htmlFor="reset-confirm">
                            Confirmar nova senha
                        </label>
                        <input
                            id="reset-confirm"
                            data-testid="reset-confirm-input"
                            type="password"
                            required
                            minLength={8}
                            value={form.confirm}
                            onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
                            className="mt-2 w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-stone-500 focus:border-gold"
                            placeholder="Repita a senha"
                            autoComplete="new-password"
                        />

                        {error && (
                            <p data-testid="reset-error" className="mt-4 rounded-xl bg-red-500/15 px-4 py-2.5 text-xs font-medium text-red-300">
                                {error}
                            </p>
                        )}

                        <button
                            type="submit"
                            data-testid="reset-submit-btn"
                            disabled={busy}
                            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-sm font-semibold text-forest-deep transition-all duration-200 hover:bg-sun disabled:opacity-60 active:scale-[0.98]"
                        >
                            <Lock className="h-4 w-4" />
                            {busy ? "Salvando..." : "Salvar nova senha"}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
